import React, {  Dispatch, SetStateAction, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiInfo } from "react-icons/fi";
import { AppContext } from "../../../context/AppContext";
import "./searchPage.css";

import { PiCalendarDotsLight  } from "react-icons/pi";
import {  Coordonates, FilterValue, formatFilterLabel, getCuisineDisplayLabel, getVisibleFilters, TIME_FILTERS } from "../../../components/types/types";
import { useLocation, useNavigate } from "react-router-dom";
import { MdRestartAlt } from "react-icons/md";
import { countMiniRestoResults, CurrentCity, getAllPresentFilterFromMiniRestos, getContextualMiniRestos, getInitialCurrentCity, getRandomSuggestions, getVisibleDateHour, MultiSearchResult, useSeoSearchAnchor } from "./hookSearchPage";
import { TfiClose } from "react-icons/tfi";
import { RestaurantContext } from "../../../context/RestaurantContext";
import CantProximity from "../../../components/cantProximity/cantProximity";
import CalendarMobile from "./components/calendarMobile";
import { HookCitySearchMobile, HookFilterMobile, HookMultiSearchMobile } from "./hookSearchValueMobile";

const isSameFilter = (a: FilterValue, b: FilterValue): boolean => {
    if (Array.isArray(a) && Array.isArray(b)) {
        return a[0] === b[0] && a[1] === b[1];
    }

    return a === b;
};
const removeTimeFilters = (filters: FilterValue[]) => {
    return filters.filter(
        filter => typeof filter !== "string" || !TIME_FILTERS.has(filter)
    );
};

interface SearchPageProps {
    routeCity:string,
    setCurrentNeighborhood:Dispatch<SetStateAction<string[]>>,
}

const SearchPage:React.FC<SearchPageProps> = ({routeCity,setCurrentNeighborhood}) => {
    const { coordonates,setCoordonates,indexablePages,openSearchPage,date_selected,hour_selected,filterChoose,currentNeighborhood,setOpenSearchPage,setFilterChoose, setDate_selected, setHour_selected } = useContext(AppContext);
    const {miniRestos,miniRestosLoaded,fetchMiniRestos,resetCityRestaurants} = useContext(RestaurantContext)
    const panelRef = useRef<HTMLDivElement>(null);
    const refCloseSearch = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const location = useLocation();

    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        if (!openSearchPage) {
            setIsVisible(false);
            return;
        }
        const raf = requestAnimationFrame(() => {
            setIsVisible(true);
        });
        return () => cancelAnimationFrame(raf);
    }, [openSearchPage]);

    useEffect(() => {
        if (!openSearchPage) return;
        fetchMiniRestos();
    }, [openSearchPage, fetchMiniRestos]);

    const suggestions = useMemo<MultiSearchResult[]>(() => {
        if (!openSearchPage) return [];
        return getRandomSuggestions(miniRestos, 7).map(resto => ({
            kind: "restaurant",
            label: resto.name,
            restaurant: resto
        }));
    }, [miniRestos,openSearchPage]);

    const [currentDate,setCurrentDate] = useState<Date |null>(null)
    const [currentHour,setCurrentHour] = useState('')

    const [currentFilter, setCurrentFilter] = useState<(string  |[number,number])[]>(filterChoose)

    const [currentCity,setCurrentCity] = useState<CurrentCity | null>(null)
    const [impossibleCity,setImpossibleCity] = useState(false)
    const initialNeighborhoodRef = useRef<string[]>(currentNeighborhood);
    const initialRouteCityRef = useRef(routeCity);
    useEffect(() => {
        const currentRouteCity = getInitialCurrentCity(routeCity, indexablePages);
        const isStillOnInitialCity = !currentCity || currentCity?.slug === currentRouteCity?.slug;
        if (!isStillOnInitialCity) return;

        initialNeighborhoodRef.current = [...currentNeighborhood];
        initialRouteCityRef.current = routeCity;
    }, [routeCity,currentNeighborhood,currentCity,indexablePages]);
    const handleSetCurrentCity: React.Dispatch< React.SetStateAction<CurrentCity | null>> = (value) => {
        setCurrentCity(prev => {
            const next = typeof value === "function"? value(prev): value;
            if (!next) {
                setCurrentNeighborhood([...initialNeighborhoodRef.current]);
            }

            return next;
        });
    };
    useEffect(() => {
        if (!impossibleCity) return;
        const timeout = setTimeout(() => {
            setImpossibleCity(false);
        }, 4000);
        return () => clearTimeout(timeout);
    }, [impossibleCity]);



    const [partSelect,setPartSelect] = useState('city')
    const contextualDistance = currentCity?.slug === "restaurants-a-proximite" ? 5 : null;
    const contextualMiniRestos = useMemo(() => {
        if (!miniRestosLoaded) return [];
        return getContextualMiniRestos(
            miniRestos,
            currentCity,
            currentDate,
            currentHour,
            coordonates,
            contextualDistance,
            currentNeighborhood
        );
    }, [miniRestosLoaded, miniRestos, currentCity, currentDate, currentHour,coordonates,contextualDistance,currentNeighborhood]);

    const defaultPresentSearchFilters = useMemo(() => {
        return getAllPresentFilterFromMiniRestos(miniRestos);
    }, [miniRestos]);
    const presentSearchFilters = useMemo(() => {
        if (!miniRestosLoaded) return defaultPresentSearchFilters;
        if (partSelect !== "filter") return defaultPresentSearchFilters;

        if(contextualMiniRestos.length === 0) return defaultPresentSearchFilters;
        return getAllPresentFilterFromMiniRestos(contextualMiniRestos);
    }, [miniRestosLoaded,partSelect,contextualMiniRestos,defaultPresentSearchFilters]);

    const resultCount = useMemo(() => {
        if (!miniRestosLoaded) return null;
        return countMiniRestoResults(
            contextualMiniRestos,
            currentFilter,
            presentSearchFilters
        );
    }, [miniRestosLoaded, contextualMiniRestos, currentFilter,presentSearchFilters]);

    useEffect(() => {
        if (!openSearchPage) return;

        const initialCity = getInitialCurrentCity(routeCity, indexablePages);

        setCurrentCity(initialCity);
        setCurrentDate(date_selected);
        setCurrentHour(hour_selected);
        setCurrentFilter(filterChoose);

        if (initialCity && date_selected) {setPartSelect("filter");} 
        else if (initialCity) {setPartSelect("date");} 
        else {setPartSelect("city");}
    }, [openSearchPage,routeCity,indexablePages,date_selected,hour_selected,filterChoose]);
    const getDefaultPartSelect = useCallback(() => {
        const useCity = getInitialCurrentCity(routeCity, indexablePages);
        if (useCity && date_selected) return "filter";
        if (useCity) return "date";
        return "city";
    }, [routeCity, indexablePages, date_selected]);
    useEffect(() => {
        if (openSearchPage) return;
        const timeout = window.setTimeout(() => {
            setPartSelect(getDefaultPartSelect());
        }, 340);
        return () => {
            window.clearTimeout(timeout);
        };
    }, [openSearchPage, getDefaultPartSelect]);
    const savedScrollYRef = useRef(0);
    useLayoutEffect(() => {
        if (!openSearchPage) return;

        savedScrollYRef.current = window.scrollY;

        document.body.classList.add("scroll-lock");
        document.body.style.top = `-${savedScrollYRef.current}px`;

        return () => {
            document.body.classList.remove("scroll-lock");
            document.body.style.top = "";

            window.scrollTo(0, savedScrollYRef.current);
        };
    }, [openSearchPage]);

    const handlePart = (value:string) => {
        setPartSelect(value)
    }

    const handleSuggestionCity = (city: CurrentCity) => {
        if((city?.name || '') === 'À proximité') {
            handleTestLoc(city)
            return;
        }
        if (city?.slug === initialRouteCityRef.current) setCurrentNeighborhood([...initialNeighborhoodRef.current]);
        else setCurrentNeighborhood([]);

        setCurrentCity(city);
        setPartSelect('date');
    };
    const [showCantSearch,setShowCantSearch] = useState(false)
    useEffect(() => {
        if (!showCantSearch) return;
        const timeout = setTimeout(() => {
            setShowCantSearch(false);
        }, 5000);
        return () => clearTimeout(timeout);
    }, [showCantSearch]);
    const {shouldKeepCurrentSeoPath} = useSeoSearchAnchor({pathname: location.pathname,indexablePages,currentFilter,currentCitySlug: currentCity?.slug ?? null,});

    const handleLaunchSearch = () => {
        if(!currentDate && currentFilter.length === 0 && !currentHour && !currentCity) {
            setDate_selected(null);
            setHour_selected("");
            setFilterChoose([]);
            setCurrentNeighborhood([])
            navigate("/restaurants", {
                state: { topBarMode: "search" }
            });

            setOpenSearchPage(false);
            return;
        }
        if(resultCount === 0) {
            setShowCantSearch(true)
            return;
        }
        setHour_selected(currentHour)
        setDate_selected(currentDate)

        setFilterChoose(currentFilter) 
        if (shouldKeepCurrentSeoPath) {
            setOpenSearchPage(false);
            return;
        }
        if (currentCity?.slug) {
            const hasKnownCoordinates = currentCity.name !== "À proximité" && currentCity.latitude && currentCity.longitude;
            navigate(`/${currentCity.slug}`, {
                state: { topBarMode: "search",
                    ...(hasKnownCoordinates
                    ? {
                        knownCity: {
                            name: currentCity.name,
                            cityNameRaw: currentCity.cityNameRaw ?? currentCity.name,
                            slug: currentCity.slug,
                            latitude: currentCity.latitude,
                            longitude: currentCity.longitude
                        }
                    }
                    : {})
                 }
            });
        }else {
            if (routeCity) {
                resetCityRestaurants();
            }
            navigate('/', {
                state: { topBarMode: "search" }
            });
        }
        setOpenSearchPage(false);
    };
    const closeSearchPage = () => {
        setCurrentCity(null);
        setPartSelect('city');
        setOpenSearchPage(false);
    };
    const canResetAll = currentCity || currentDate || currentHour || currentFilter.length > 0
    const resetAll = () => {
        setCurrentCity(null);
        setCurrentDate(null)
        setCurrentHour('')
        setCurrentFilter([])
        setCurrentNeighborhood([...initialNeighborhoodRef.current]);
    }
    const getShortcutTimeFilter = useCallback((date: Date | null, hour: string): string | null => {
        if (!date || !hour) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(date);
        selectedDate.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const isToday = selectedDate.getTime() === today.getTime();
        const isTomorrow = selectedDate.getTime() === tomorrow.getTime();
        if (isToday) {
            if (hour === "12h30") return "Ouvert ce midi";
            if (hour === "20h00") return "Ouvert ce soir";
        }
        if (isTomorrow) {
            if (hour === "12h30") return "Ouvert demain midi";
            if (hour === "20h00") return "Ouvert demain soir";
        }
        return null;
    },[]);
    useEffect(() => {
        const shortcutFilter = getShortcutTimeFilter(currentDate, currentHour);
        setCurrentFilter(prev => {
            const withoutTimeFilters = removeTimeFilters(prev);
            if (!shortcutFilter) return withoutTimeFilters;
            return [...withoutTimeFilters, shortcutFilter];
        });
    }, [currentDate, currentHour, getShortcutTimeFilter]);
    const canGoToFilter = useCallback((nextDate: Date | null, nextHour: string) => {
        if (!miniRestosLoaded) return false;
        const nextContextualRestos = getContextualMiniRestos(miniRestos,currentCity,nextDate,nextHour,coordonates,contextualDistance,currentNeighborhood);
        const shortcutFilter = getShortcutTimeFilter(nextDate,nextHour);
        const filtersWithoutTime = removeTimeFilters(currentFilter);
        const nextFilters = shortcutFilter ? [...filtersWithoutTime, shortcutFilter] : filtersWithoutTime;
        const nextCount = countMiniRestoResults(nextContextualRestos,nextFilters,presentSearchFilters);
        if (nextCount === 0) {
            setShowCantSearch(true);
            return false;
        }
        setShowCantSearch(false);
        return true;
    },[miniRestosLoaded,miniRestos,currentCity,coordonates,contextualDistance,currentFilter,presentSearchFilters,getShortcutTimeFilter,currentNeighborhood]);
    const handleHour = useCallback((time: string) => {
        const nextDate = currentDate ?? new Date();
        if (!currentDate) setCurrentDate(nextDate);
        const nextHour = currentHour === time ? '' : time;
        setCurrentHour(nextHour);
        if (nextHour && canGoToFilter(nextDate, nextHour)) setPartSelect('filter');
    }, [currentDate, currentHour,canGoToFilter]);
    const handleClickDay = useCallback((day: Date) => {
        const nextDate = (currentDate?.getTime() === day.getTime()) ? null : day;
        setCurrentDate(nextDate);
        if (!nextDate) {
            setCurrentHour("");
            setShowCantSearch(false);
            return;
        }
        const canGo = canGoToFilter(nextDate, currentHour);
        if (currentHour && canGo) setPartSelect('filter');
    }, [currentDate, currentHour,canGoToFilter]);


    const [overflowVisible, setOverflowVisible] = useState(false);
    useEffect(() => {
        if (partSelect !== 'date') {
            setOverflowVisible(false);
            return;
        }
        const t = setTimeout(() => setOverflowVisible(true), 300);
        return () => clearTimeout(t);
    }, [partSelect]);
    const updateCurrentFilter = useCallback((updater: React.SetStateAction<FilterValue[]>) => {
        setCurrentFilter(prev => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            const hadTimeFilter = prev.some(
                filter => typeof filter === "string" && TIME_FILTERS.has(filter)
            );
            const hasTimeFilter = next.some(
                filter => typeof filter === "string" && TIME_FILTERS.has(filter)
            );
            if (hadTimeFilter && !hasTimeFilter) {
                setCurrentDate(null);
                setCurrentHour("");
            }
            return next;
        });
    }, []);
    const addFilter = useCallback((value: FilterValue) => {
        const alreadySelected = currentFilter.some(current => isSameFilter(current, value));
        if (alreadySelected &&typeof value === "string" &&TIME_FILTERS.has(value)) {
            setCurrentDate(null);
            setCurrentHour('');
        }
        setCurrentFilter(prev => {
            const isSelected = prev.some(current => isSameFilter(current, value));
            if (isSelected) {
                return prev.filter( current => !isSameFilter(current, value) );
            }
            return [...prev, value];
        });
    }, [currentFilter]);

    const applyFilterGroup = useCallback(( groupValues: FilterValue[], selectedValues: FilterValue[] ) => {
        const removedTimeFilter = currentFilter.some(current => {
            if (typeof current !== "string" ||!TIME_FILTERS.has(current)) return false;
            const belongsToGroup = groupValues.some(groupValue => isSameFilter(groupValue, current));
            const stillSelected = selectedValues.some(selectedValue => isSameFilter(selectedValue, current));
            return belongsToGroup && !stillSelected;
        });
        if (removedTimeFilter) {
            setCurrentDate(null);
            setCurrentHour('');
        }
        setCurrentFilter(prev => {
            const withoutGroup = prev.filter(
                current => !groupValues.some(groupValue =>isSameFilter(groupValue, current))
            );
            return [...withoutGroup, ...selectedValues];
        });
    }, [currentFilter]);

    const handleContentTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const active = document.activeElement as HTMLElement | null;
        if (active && (active.tagName === "INPUT") && !active.contains(e.target as Node)) {
            active.blur();
        }
    };


    const [cantProximity,setCantProximity] = useState(false)
    const handleTestLoc = (city?: CurrentCity) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
            (position) => {
                const userLocation: Coordonates = {
                latitude: position.coords.latitude.toString(),
                longitude: position.coords.longitude.toString(),
                };
                setCoordonates(userLocation);
                setCurrentNeighborhood([]);
                if (city) {
                    setCurrentCity(city);
                    setPartSelect('date');
                }
            },
            () => {
                setCantProximity(true);
            }
            );
        }
    }
    useEffect(() => {
        if(cantProximity) setCurrentCity(null)
    },[cantProximity])
    const visibleCurrentFilters = useMemo(() => {
        return getVisibleFilters(currentFilter);
    }, [currentFilter]);
    const visibleFilterSummary = useMemo(() => {
        const MAX_CHARACTERS = 25;
        if (visibleCurrentFilters.length === 0) return "(Type, ambiance, expérience)";
        const labels = visibleCurrentFilters.map((filter) => getCuisineDisplayLabel(formatFilterLabel(filter)));
        let result = "";
        for (let index = 0; index < labels.length; index++) {
            const label = labels[index];
            const separator = result ? ", " : "";
            const fullCandidate = `${result}${separator}${label}`;
            if (fullCandidate.length <= MAX_CHARACTERS) {
                result = fullCandidate;
                continue;
            }

            const remainingAfterCurrent = labels.length - index - 1;
            const counter = remainingAfterCurrent > 0 ? ` +${remainingAfterCurrent}` : "";
            const ending = `...${counter}`;

            const availableCharacters = MAX_CHARACTERS - result.length - separator.length - ending.length;
            if (availableCharacters > 0) {
                const partialLabel = label.slice(0, availableCharacters);
                return `(${result}${separator}${partialLabel}${ending})`;
            }
            const fullyHiddenCount = labels.length - index;
            const hiddenSuffix = fullyHiddenCount > 0 ? `... +${fullyHiddenCount}` : "...";
            return `(${result}${hiddenSuffix})`;
        }
        return `(${result})`;
    }, [visibleCurrentFilters]);


    const [showMultiSearch,setShowMultiSearch] = useState(false)

    return (
        <>
            <div className={`searchPageOverlay ${isVisible ? "open" : ""}`} onClick={closeSearchPage} />
            <div ref={panelRef} className={`containerSearchPage  ${isVisible ? "open" : ""}`} onTouchStart={handleContentTouchStart}  role="dialog" aria-modal="true" aria-label="Recherche">
                <div className="shadowGlobalTop"></div>
                <div ref={refCloseSearch} className={`containerIconClose ${showMultiSearch ? 'isHidden' : ''}`}><TfiClose size={40} className="iconClose" onClick={() => {if(showMultiSearch){setShowMultiSearch(false)}else{closeSearchPage()}}}/></div>
                {impossibleCity && <div className="containerCantCity smallText">
                    <FiInfo className='icon' />
                    <span>QuelResto n'est pas encore présent dans cette ville.</span>
                </div>}
                <HookMultiSearchMobile showMultiSearch={showMultiSearch} setShowMultiSearch={setShowMultiSearch} suggestions={suggestions} miniRestos={miniRestos}  refCloseSearch={refCloseSearch}/>
                <div className="searchPageContent" >
                    <HookCitySearchMobile partSelect={partSelect} setPartSelect={setPartSelect} currentCity={currentCity} setCurrentCity={handleSetCurrentCity} miniRestos={miniRestos} handleSuggestionCity={handleSuggestionCity} setImpossibleCity={setImpossibleCity} openSearchPage={openSearchPage}/>
                    <div className={`elemContent datePart ${partSelect === 'date' ? 'isOpen' : ''}`} style={overflowVisible ? { overflow: 'visible' } : undefined}>
                        <div className="basePart" onClick={() => handlePart('date')}>
                            <PiCalendarDotsLight  size={15} className="icon"/>
                            <div className="basePartTextWrapper">
                                <span className='textBase smallText'>{getVisibleDateHour(currentDate,currentHour)}</span>
                            </div>
                            {partSelect === 'date' && <span className={`textReinit XsmallText pointer flexAllCenter ${(currentDate || currentHour) ? 'isActive' : ''}`}  onClick={() => {setCurrentDate(null);setCurrentHour('')}}>
                                <MdRestartAlt size={16} className="icon"/>
                                Réinitialiser
                            </span>}
                            {((currentDate || currentHour) && partSelect !== 'date' ) && <TfiClose className="iconReset" size={16} onClick={(e) => {e.stopPropagation();setCurrentDate(null);setCurrentHour('')}} />}
                        </div>
                        {partSelect === 'date' && <>
                        <div className="contentHour">
                            <CalendarMobile handleClickDay={handleClickDay} currentDate={currentDate} />
                            <div className="selectHourPart XsmallText">
                                <span className="XsmallTextBold">Service : </span>
                                <div className={`smallWhiteRoundButton ${currentHour==='12h30' ? 'isSelect' : ''}`} onClick={() => handleHour('12h30')}>Midi</div>
                                <span className="flexAllCenter">ou</span>
                                <div className={`smallWhiteRoundButton ${currentHour==='20h00' ? 'isSelect' : ''}`} onClick={() => handleHour('20h00')}>Soir</div>
                            </div>
                        </div>
                        </>}
                    </div>
                    <HookFilterMobile partSelect={partSelect} setPartSelect={setPartSelect} currentFilter={currentFilter} visibleCurrentFilters={visibleCurrentFilters} setCurrentFilter={updateCurrentFilter} presentSearchFilters={presentSearchFilters} contextualMiniRestos={contextualMiniRestos} miniRestosLoaded={miniRestosLoaded} visibleFilterSummary={visibleFilterSummary} addFilter={addFilter} applyFilterGroup={applyFilterGroup} />
                </div>
                <div className="bottomPart XsmallText" style={showMultiSearch ? {opacity:0} : {}}>
                    <div className="midWhiteRoundButton pointer" onClick={() => {if(canResetAll){resetAll()}else{closeSearchPage()}}}>{canResetAll ? 'Tout effacer' : 'Annuler'}</div>
                    <div className={`midGoldRoundButton pointer ${(resultCount === 0) ? 'isLock' : ''}`} onClick={handleLaunchSearch}>
                        {!miniRestosLoaded ? (
                            <>Rechercher <span className="countSpinnerLoad" /></>
                        ) : resultCount !== null && (currentCity || currentDate || currentHour || currentFilter.length > 0) ? (
                            <>Rechercher <span className="resultCount">{`(${resultCount} résultat${resultCount > 1 ? 's':''})`}</span></>
                        ) : (
                            <>Rechercher <span className="resultCount">{`(${miniRestos.length} résultat${miniRestos.length > 1 ? 's':''})`}</span></>
                        )}
                    </div>
                    {showCantSearch && resultCount === 0 && <div className="containerCantSearch smallText">
                        <FiInfo className='icon' />
                        <span>Aucun résultat trouvé. {currentFilter.length > 1 ? 'Essayez d’enlever un ou plusieurs filtres.' : 'Essayez de modifier votre recherche.'}</span>
                    </div>}
                </div>
                {cantProximity ? <CantProximity isForProximity={true} setCantProximity={setCantProximity} canApplyCity={handleSuggestionCity} canClose={true}/> : undefined}
            </div>
        </>
    );
};

export default SearchPage;